import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, User, CreditCard, Shield, RefreshCw, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useState } from "react";
import { getPlanName } from "@/config/pricing";

const AccessTest = () => {
  const { user, subscription, isLoading, checkSubscription, isAdmin } = useAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await checkSubscription();
    setIsRefreshing(false);
    toast.success("Subscription status refreshed");
  };

  const handleManageSubscription = async () => {
    setIsOpeningPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.error === "no_customer") {
        toast.info(data.message || "No billing history found");
        return;
      }
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to open subscription portal");
    } finally {
      setIsOpeningPortal(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const isAuthenticated = !!user;
  const hasActiveSubscription = subscription.subscribed;
  const accessGranted = isAuthenticated && hasActiveSubscription;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold mb-2">Access Test</h1>
          <p className="text-muted-foreground">
            Verify your authentication and subscription status
          </p>
        </div>
        <Button onClick={handleRefresh} variant="outline" disabled={isRefreshing}>
          <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh Status
        </Button>
      </div>

      {/* Overall Status */}
      <Card className={accessGranted ? "border-green-500/50 bg-green-500/5" : "border-destructive/50 bg-destructive/5"}>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            {accessGranted ? (
              <CheckCircle className="w-12 h-12 text-green-500" />
            ) : (
              <XCircle className="w-12 h-12 text-destructive" />
            )}
            <div className="flex-1">
              <h2 className="text-2xl font-bold">
                {accessGranted ? "Access Granted" : "Access Denied"}
              </h2>
              <p className="text-muted-foreground">
                {accessGranted 
                  ? "You have full access to all AI StoryCraft Studio apps" 
                  : "Some requirements are not met"}
              </p>
            </div>
            {hasActiveSubscription && (
              <Button onClick={handleManageSubscription} variant="outline" disabled={isOpeningPortal}>
                <ExternalLink className="w-4 h-4 mr-2" />
                Manage Subscription
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-3">
        {/* Authentication Status */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="w-5 h-5" />
              Authentication
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Status</span>
                <Badge variant={isAuthenticated ? "default" : "destructive"}>
                  {isAuthenticated ? "Authenticated" : "Not Authenticated"}
                </Badge>
              </div>
              {user && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Email</span>
                    <span className="text-sm font-mono truncate max-w-[150px]">{user.email}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">User ID</span>
                    <span className="text-xs font-mono truncate max-w-[120px]">{user.id}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Role</span>
                    <Badge variant={isAdmin ? "secondary" : "outline"}>
                      {isAdmin ? "Admin" : "Member"}
                    </Badge>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Subscription Status */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <CreditCard className="w-5 h-5" />
              Subscription
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Status</span>
                <Badge variant={hasActiveSubscription ? "default" : "destructive"}>
                  {subscription.status || (hasActiveSubscription ? "Active" : "Inactive")}
                </Badge>
              </div>
              {subscription.subscribed && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Plan</span>
                    <Badge variant="secondary">
                      {subscription.isLifetime ? "Lifetime" : getPlanName(subscription.productId)}
                    </Badge>
                  </div>
                  {subscription.subscriptionEnd && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Renews</span>
                      <span className="text-sm">
                        {new Date(subscription.subscriptionEnd).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </>
              )}
              {subscription.stripeCustomerId && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Customer</span>
                  <span className="text-xs font-mono truncate max-w-[100px]">
                    {subscription.stripeCustomerId}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Access Rights */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Shield className="w-5 h-5" />
              App Access
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { name: "Nexis AI", access: accessGranted },
                { name: "ClipAI", access: accessGranted },
                { name: "Monetization AI", access: accessGranted },
                { name: "AskFint", access: accessGranted },
                { name: "StoryCraft Formula", access: accessGranted },
              ].map((app) => (
                <div key={app.name} className="flex items-center justify-between">
                  <span className="text-muted-foreground">{app.name}</span>
                  {app.access ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-destructive" />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Debug Info */}
      <Card>
        <CardHeader>
          <CardTitle>Debug Information</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="bg-muted p-4 rounded-lg overflow-auto text-xs">
            {JSON.stringify(
              {
                authenticated: isAuthenticated,
                subscription: {
                  subscribed: subscription.subscribed,
                  plan: subscription.plan,
                  status: subscription.status,
                  productId: subscription.productId,
                  subscriptionEnd: subscription.subscriptionEnd,
                  stripeCustomerId: subscription.stripeCustomerId,
                  stripeSubscriptionId: subscription.stripeSubscriptionId,
                },
                accessGranted: accessGranted,
                isAdmin: isAdmin,
                user: user ? { 
                  id: user.id, 
                  email: user.email,
                  name: user.user_metadata?.full_name || null,
                  createdAt: user.created_at,
                } : null,
              },
              null,
              2
            )}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccessTest;
